import { useState } from 'react';
import './SaveQueryModal.css';

interface SaveQueryModalProps {
  query: string;
  datacenter: string;
  onSave: (name?: string, description?: string) => void;
  onCancel: () => void;
}

/**
 * Modal for saving a query
 * Allows user to provide custom name and description
 */
export const SaveQueryModal = ({ query, datacenter, onSave, onCancel }: SaveQueryModalProps) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const handleSave = () => {
    onSave(name.trim() || undefined, description.trim() || undefined);
  };

  const _handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCancel();
    } else if (e.key === 'Enter' && e.metaKey) {
      handleSave();
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={onCancel}
      onKeyDown={(e) => e.key === 'Escape' && onCancel()}
      role="button"
      tabIndex={0}
      aria-label="Close modal"
    >
      <div className="modal-content" role="dialog" aria-modal="true">
        <div className="modal-header">
          <h2>Save Query</h2>
          <button type="button" className="modal-close" onClick={onCancel} aria-label="Close">
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="form-group">
            <label htmlFor="query-preview">SQL Query</label>
            <div className="query-preview">
              {query.substring(0, 300)}
              {query.length > 300 && '...'}
            </div>
            <div className="query-meta">
              <span className="datacenter-badge">{datacenter}</span>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="query-name">
              Name <span className="optional">(optional)</span>
            </label>
            <input
              id="query-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Leave blank to auto-generate"
              maxLength={255}
              aria-label="Query name"
            />
            <small className="help-text">If blank, a name will be generated from your query</small>
          </div>

          <div className="form-group">
            <label htmlFor="query-description">
              Description <span className="optional">(optional)</span>
            </label>
            <textarea
              id="query-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add notes about what this query is for..."
              rows={3}
              aria-label="Query description"
            />
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSave}>
            💾 Save Query
          </button>
        </div>

        <div className="modal-hint">
          Press <kbd>Esc</kbd> to cancel or <kbd>⌘+Enter</kbd> to save
        </div>
      </div>
    </div>
  );
};
